import { WalletError } from '@cef-ai/wallet-api-client';
import { makeAutoObservable, runInAction } from 'mobx';

/**
 * What the popup-side `wallet:hello{intent:'register'}` handler is waiting
 * for: a host asked for a wallet, and nothing can happen until a human has
 * proven a mailbox and run the passkey ceremony.
 *
 * `label` is the credential label the host may have put on `wallet:hello`; it
 * rides through to `Identity.register({ label })` (the passkey's device
 * label). The host does NOT supply the email or the display name — the wallet
 * origin collects both, because only the wallet origin can mint the
 * `emailToken` that proves the address.
 */
export interface RegistrationRequest {
  /** Origin of the host page that called `EmbedWallet.register()`. */
  hostOrigin: string;
  /** The host app's display name, from `wallet:hello`'s `appContext`. */
  appName: string;
  /** Optional credential label supplied by the host. */
  label?: string;
}

/**
 * How a registration prompt ended.
 *
 * `completed` means a session now exists on this identity — either a wallet
 * was created (email → OTP → passkey) or, on the already-registered branch,
 * the reader signed in to the wallet that address already has. Either way the
 * handler may read `identity.addresses`/`credentialId` and reply
 * `wallet:login:ok`.
 *
 * Task 24: `completed` carries WHICH of those two it was, in `accountCreated`.
 * The prompt is the only place that knows — it ran one branch or the other —
 * and it knows only while it is running. So the bit is captured at the
 * resolving call site and handed to the handler, which puts it on
 * `wallet:login:ok`. It is deliberately a REQUIRED field of the outcome, not
 * an optional one: every `resolveCurrent({ kind: 'completed' })` call site
 * must state which path it is, and a new completed path added later fails to
 * compile until it does. (On the wire it is optional, because an older wallet
 * deployment genuinely cannot say — that is a different question from a call
 * site here declining to.)
 *
 * `cancelled` means no session was created and none will be: the reader
 * closed the flow. The handler turns it into a NAMED `wallet:error`, never a
 * promise the host waits on forever.
 *
 * There is deliberately no `failed` outcome. A failure mid-flow is not an
 * ending — the prompt keeps the reader on the step whose button can recover
 * it (a fresh code, a retried ceremony, a different address), exactly as
 * `/register` does. The only ways out are "a session exists" and "the reader
 * stopped".
 */
export type RegistrationOutcome =
  | {
      kind: 'completed';
      /**
       * `true` when this flow CREATED the account (`identity.register()`
       * succeeded); `false` when it signed in to a wallet the address already
       * had (`identity.login()` from the `already-registered` step or the
       * "Already have a wallet? Sign in" footer).
       */
      accountCreated: boolean;
    }
  | { kind: 'cancelled' };

export interface PendingRegistration {
  /**
   * Identifies THIS prompt. The React view keys its step machine on it, so a
   * second prompt in the same popup (a host that calls `register()` again over
   * a reused `PopupTransport`) starts on a blank email step instead of
   * inheriting the previous attempt's address, code and error.
   *
   * A local counter rather than `crypto.randomUUID()`: it is a React key, not
   * a security or correlation value, and a plain counter keeps the store
   * usable in a bare Node test environment.
   */
  id: string;
  request: RegistrationRequest;
  resolve: (o: RegistrationOutcome) => void;
}

/**
 * Holds at most one in-flight embedded-registration prompt.
 *
 * Same seam as `PendingConsentStore`, for the same reason: `createHelloHandler`
 * is a MESSAGE handler with no way to render anything, and the registration
 * ceremony now has to be preceded by two screens of human input. So the
 * handler calls `request()` and awaits; the React `<RegisterPrompt>` observer
 * renders while `pending !== null` and calls `resolveCurrent()` when the flow
 * ends. Keeping the store this thin is what keeps the ordering guarantee
 * checkable: it cannot resolve `completed` on its own, and the handler cannot
 * proceed on anything else.
 */
export class PendingRegistrationStore {
  pending: PendingRegistration | null = null;
  private seq = 0;

  constructor() {
    makeAutoObservable(this);
  }

  request(req: RegistrationRequest): Promise<RegistrationOutcome> {
    if (this.pending) {
      // A `WalletError`, not a bare `Error` (fix round 1). This throw travels
      // out through `createHelloHandler` to `PopupHostBridge`, which maps a
      // `WalletError` to its own code and everything else to `internal` — so a
      // bare `Error` reached the host as "something went wrong on our side"
      // for a condition that is neither a server fault nor unknown. It is also
      // genuinely REACHABLE, unlike the unsupported-surface case: a host may
      // call `register()` again over a reused `PopupTransport` while the
      // reader still has a half-finished flow in the popup. The reader's
      // remedy — finish that flow, or close the window — only survives if the
      // code names the situation.
      throw new WalletError(
        'registration-in-progress',
        'PendingRegistrationStore: a registration prompt is already in flight; finish or close the open one',
      );
    }
    const id = `registration-${++this.seq}`;
    return new Promise<RegistrationOutcome>((resolve) => {
      runInAction(() => {
        this.pending = { id, request: req, resolve };
      });
    });
  }

  resolveCurrent(outcome: RegistrationOutcome): void {
    const current = this.pending;
    if (!current) return;
    runInAction(() => {
      this.pending = null;
    });
    current.resolve(outcome);
  }
}
